"use client";

import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import styles from "@assets/styles/modal.module.scss";

// Reference counter for tracking open modals
let modalCount = 0;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  size?: "sm" | "md" | "lg" | "xl" | "full" | "custom";
  variant?: "default" | "video" | "form" | "demo";
  maxWidth?: string;
  maxHeight?: string;
  className?: string;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  fullscreenOnMobile?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  size = "md",
  variant = "default",
  maxWidth,
  maxHeight,
  className = "",
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  fullscreenOnMobile = false,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Size configurations with responsive design
  const sizeConfig = {
    sm: "max-w-sm md:max-w-md",
    md: "max-w-md md:max-w-lg",
    lg: "max-w-lg md:max-w-xl lg:max-w-2xl",
    xl: "max-w-xl md:max-w-2xl lg:max-w-3xl",
    full: "max-w-full",
    custom: "",
  };

  // Height configurations with responsive design
  const heightConfig = {
    sm: "max-h-96 md:max-h-[28rem]",
    md: "max-h-[32rem] md:max-h-[36rem]",
    lg: "max-h-[40rem] md:max-h-[44rem]",
    xl: "max-h-[48rem] md:max-h-[52rem]",
    full: "max-h-full",
    custom: "",
  };

  // Variant-specific configurations
  const getVariantClasses = () => {
    switch (variant) {
      case "video":
        return "max-w-4xl max-h-[80vh] md:max-h-[85vh]";
      case "form":
        return "max-w-2xl h-auto max-h-[85vh] md:max-h-[90vh]";
      case "demo":
        return "max-w-xl max-w-2xl md:max-w-3xl h-auto max-h-[85vh] md:max-h-[90vh]";
      default:
        return "";
    }
  };

  // Mobile fullscreen classes
  const getMobileClasses = () => {
    if (fullscreenOnMobile) {
      return "md:rounded-2xl md:shadow-2xl rounded-none shadow-none";
    }
    return "rounded-2xl shadow-2xl";
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeOnEscape) {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        overlayRef.current &&
        e.target === overlayRef.current &&
        closeOnOverlayClick
      ) {
        onClose();
      }
    };

    // Store current scroll position before modal opens
    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;

    // Create a more stable scroll lock
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    // Store original paddingRight value before modifying it
    const originalPaddingRight = document.body.style.paddingRight;
    const hadPaddingRight = originalPaddingRight !== "";

    // Increment modal counter
    modalCount++;
    const isFirstModal = modalCount === 1;
    
    if (isFirstModal) {
      // Store original paddingRight value for restoration (only first modal needs to do this)
      if (hadPaddingRight) {
        document.body.setAttribute("data-original-padding-right", originalPaddingRight);
      } else {
        // Mark that there was no original paddingRight
        document.body.setAttribute("data-original-padding-right", "");
      }
      
      // Add CSS class for modal open state (only for first modal)
      document.body.classList.add("modal-open");
    }

    // Only set body styles for the first modal
    if (isFirstModal) {
      // Store scroll position in data attributes for restoration
      document.body.setAttribute("data-scroll-y", currentScrollY.toString());
      document.body.setAttribute("data-scroll-x", currentScrollX.toString());

      // Prevent body scroll with better approach
      document.body.style.position = "fixed";
      document.body.style.top = `-${currentScrollY}px`;
      document.body.style.left = `-${currentScrollX}px`;
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      document.body.style.overflow = "hidden";

      // Prevent layout shift by compensating for scrollbar
      if (scrollBarWidth > 0) {
        // Calculate new paddingRight value (add to existing if present)
        const existingPaddingRight = hadPaddingRight
          ? parseFloat(originalPaddingRight) || 0
          : 0;
        const newPaddingRight = existingPaddingRight + scrollBarWidth;
        document.body.style.paddingRight = `${newPaddingRight}px`;
      }
    }

    // Add event listeners
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);

    // Focus trap
    if (modalRef.current) {
      modalRef.current.focus();
    }

    return () => {
      // Cleanup
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);

      // Decrement modal counter
      modalCount = Math.max(0, modalCount - 1);
      const isLastModal = modalCount === 0;

      // Only remove class and restore styles if this is the last modal
      if (isLastModal) {
        // Remove CSS class for modal open state
        document.body.classList.remove("modal-open");

        // Restore scroll position smoothly
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.width = "";
        document.body.style.height = "";
        document.body.style.overflow = "";

        // Restore paddingRight only if we're the last modal to close
        requestAnimationFrame(() => {
          const originalPaddingRightAttr = document.body.getAttribute("data-original-padding-right");
          
          if (originalPaddingRightAttr !== null) {
            if (originalPaddingRightAttr === "") {
              // There was no original paddingRight, so remove it
              document.body.style.paddingRight = "";
            } else {
              // Restore the original value
              document.body.style.paddingRight = originalPaddingRightAttr;
            }
            document.body.removeAttribute("data-original-padding-right");
          } else {
            // Fallback: if attribute is missing, just clear padding-right
            document.body.style.paddingRight = "";
          }
        });
      }

      // Get stored scroll position from data attributes
      const storedScrollY = parseInt(
        document.body.getAttribute("data-scroll-y") || "0"
      );
      const storedScrollX = parseInt(
        document.body.getAttribute("data-scroll-x") || "0"
      );

      // Clean up data attributes
      document.body.removeAttribute("data-scroll-y");
      document.body.removeAttribute("data-scroll-x");

      // Restore scroll position without jumping
      requestAnimationFrame(() => {
        window.scrollTo({
          top: storedScrollY,
          left: storedScrollX,
          behavior: "instant",
        });
      });
    };
  }, [isOpen, onClose, closeOnEscape, closeOnOverlayClick]);

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={`${styles.modalOverlay} animate-in fade-in duration-200`}
    >
      <div
        ref={modalRef}
        className={`
            ${styles.modalDialog}
            animate-in zoom-in-95 duration-200
            ${sizeConfig[size]}
            ${heightConfig[size]}
            ${getVariantClasses()}
            ${getMobileClasses()}
            ${className}
          `}
        style={{
          ...(maxWidth && { maxWidth }),
          ...(maxHeight && { maxHeight }),
        }}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        data-variant={variant}
        data-size={size}
        data-fullscreen-mobile={fullscreenOnMobile}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div
            className={`${
              styles.modalHeader
            } flex items-center justify-between ${
              variant === "demo"
                ? "py-1 px-2 border-b border-gray-100"
                : "p-4 sm:p-6 border-b border-gray-100 min-h-[56px] sm:min-h-[72px]"
            }`}
          >
            {title && (
              <h2
                className={`${styles.modalTitle} ${
                  variant === "demo" ? "text-base" : "text-xl"
                } font-semibold text-primary mb-0 ${
                  variant === "demo" || variant === "form"
                    ? "flex-1 text-center"
                    : "flex-1"
                } leading-none`}
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className={`${styles.closeButton} ${
                  variant === "demo" ? "p-1" : "p-2"
                } text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0 flex items-center justify-center`}
                aria-label="Modal schließen"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div
          className={`${styles.modalContent} ${
            variant === "form" || variant === "demo" ? "flex-none" : "flex-1"
          } overflow-y-auto w-full ${
            variant === "form" || variant === "demo" ? "h-auto" : "h-full"
          } ${
            variant === "form" || variant === "demo" ? "p-4 sm:p-6 md:p-8" : ""
          }`}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export type { ModalProps };
export default Modal;
